// @flow

import moment from 'moment'
import { createActions, handleActions } from 'redux-actions'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import userReservationResource from 'frontend_core/dist/rest/resources/userReservation'
import SMConst from 'frontend_core/dist/constants/sm-const'
import type { ReservationType, Message } from 'types/v1/reservation'
import type { RoomType } from 'types/v1/room'
import { SET_UI_STATE } from './ui'

const MESSAGES_PER_PAGE = 20

/**
 ************************************************************
 * 初期ステート
 ************************************************************
 **/

export type State = {
  detail: ReservationType,
  currentRoom: RoomType,
  messages: Array<Message>,
  nextMessagePage: number,
  hasMoreData: boolean,
  // 失敗メッセージ
  failedFetchReservationDetail: ?string,
  failedFetchMessages: ?string,
  failedSendMessage: ?string,
  successCancel: boolean,
  failedCancelMessage: ?string,
  successApproveChange: boolean,
  failedApproveChange: ?string,
}

const initialState: State = {
  detail: {},
  currentRoom: {},
  messages: [],
  nextMessagePage: 1,
  hasMoreData: false,
  // 失敗メッセージ
  failedFetchReservationDetail: null,
  failedFetchMessages: null,
  failedSendMessage: null,
  successCancel: false,
  failedCancelMessage: null,
  successApproveChange: false,
  failedApproveChange: null,
}

/**
 ************************************************************
 * ActionTypes
 ************************************************************
 **/

const actionTypes = {
  FETCH_RESERVATION_DETAIL: 'FETCH_RESERVATION_DETAIL',
  SET_RESERVATION_DETAIL: 'SET_RESERVATION_DETAIL',
  FAILED_RESERVATION_DETAIL: 'FAILED_RESERVATION_DETAIL',
  FETCH_MESSAGES: 'FETCH_MESSAGES',
  SUCCESS_FETCH_MESSAGES: 'SUCCESS_FETCH_MESSAGES',
  FAILED_FETCH_MESSAGES: 'FAILED_FETCH_MESSAGES',
  SEND_MESSAGE: 'SEND_MESSAGE',
  SUCCESS_SEND_MESSAGE: 'SUCCESS_SEND_MESSAGE',
  FAILED_SEND_MESSAGE: 'FAILED_SEND_MESSAGE',
  ADD_NEW_MESSAGE: 'ADD_NEW_MESSAGE',
  CANCEL_RESERVATION: 'CANCEL_RESERVATION',
  SUCCESS_CANCEL_RESERVATION: 'SUCCESS_CANCEL_RESERVATION',
  FAILED_CANCEL_RESERVATION: 'FAILED_CANCEL_RESERVATION',
  APPROVE_CHANGE_RESERVATION: 'APPROVE_CHANGE_RESERVATION',
  REJECT_CHANGE_RESERVATION: 'REJECT_CHANGE_RESERVATION',
  SUCCESS_APPROVE_CHANGE_RESERVATION: 'SUCCESS_APPROVE_CHANGE_RESERVATION',
  FAILED_APPROVE_CHANGE_RESERVATION: 'FAILED_APPROVE_CHANGE_RESERVATION',
}

/**
 ************************************************************
 * Actions
 ************************************************************
 **/

export const actions = createActions(
  actionTypes.FETCH_RESERVATION_DETAIL,
  actionTypes.SET_RESERVATION_DETAIL,
  actionTypes.FAILED_RESERVATION_DETAIL,
  actionTypes.FETCH_MESSAGES,
  actionTypes.SUCCESS_FETCH_MESSAGES,
  actionTypes.FAILED_FETCH_MESSAGES,
  actionTypes.SEND_MESSAGE,
  actionTypes.SUCCESS_SEND_MESSAGE,
  actionTypes.FAILED_SEND_MESSAGE,
  actionTypes.ADD_NEW_MESSAGE,
  actionTypes.CANCEL_RESERVATION,
  actionTypes.SUCCESS_CANCEL_RESERVATION,
  actionTypes.FAILED_CANCEL_RESERVATION,
  actionTypes.APPROVE_CHANGE_RESERVATION,
  actionTypes.REJECT_CHANGE_RESERVATION,
  actionTypes.SUCCESS_APPROVE_CHANGE_RESERVATION,
  actionTypes.FAILED_APPROVE_CHANGE_RESERVATION,
)

export type setReservationDetailAction = {
  payload: {
    detail: ReservationType,
    messages: Array<Message>,
  },
}

/**
 ************************************************************
 * reducer
 ************************************************************
 **/
export const reducer = handleActions(
  {
    [actions.fetchReservationDetail]: (state: State, action: ACTION_TYPE) => ({
      ...state,
      failedFetchReservationDetail: null,
    }),
    [actions.setReservationDetail]: (
      state: State,
      action: setReservationDetailAction,
    ) => ({
      ...state,
      detail: action.payload.detail,
      messages: action.payload.messages,
      nextMessagePage: 2,
      hasMoreData: action.payload.messages.length >= MESSAGES_PER_PAGE,
    }),
    [actions.failedReservationDetail]: (state: State, action: ACTION_TYPE) => ({
      ...state,
      failedFetchReservationDetail: action.payload,
    }),
    [actions.fetchMessages]: (state: State, action: ACTION_TYPE) => ({
      ...state,
      failedFetchMessages: null,
    }),
    [actions.successFetchMessages]: (state: State, action: ACTION_TYPE) => ({
      ...state,
      messages: [].concat(action.payload, state.messages),
      nextMessagePage: state.nextMessagePage + 1,
      hasMoreData: action.payload.length >= MESSAGES_PER_PAGE,
    }),
    [actions.failedFetchMessages]: (state: State, action: ACTION_TYPE) => ({
      ...state,
      failedFetchMessages: action.payload,
    }),
    [actions.sendMessage]: (state: State) => ({
      ...state,
      failedSendMessage: null,
    }),
    [actions.failedSendMessage]: (state: State, action: ACTION_TYPE) => ({
      ...state,
      failedSendMessage: action.payload,
    }),
    [actions.addNewMessage]: (state: State, action: ACTION_TYPE) => ({
      ...state,
      messages: [].concat(state.messages, action.payload),
    }),
    [actions.cancelReservation]: (state: State) => ({
      ...state,
      failedCancelMessage: null,
    }),
    [actions.successCancelReservation]: (
      state: State,
      action: ACTION_TYPE,
    ) => ({
      ...state,
      successCancel: true,
      detail: action.payload,
    }),
    [actions.failedCancelReservation]: (state: State, action: ACTION_TYPE) => ({
      ...state,
      failedCancelMessage: action.payload,
    }),
    [actions.approveChangeReservation]: (state: State) => ({
      ...state,
      failedApproveChange: null,
    }),
    [actions.rejectChangeReservation]: (state: State) => ({
      ...state,
      failedApproveChange: null,
    }),
    [actions.successApproveChangeReservation]: (
      state: State,
      action: ACTION_TYPE,
    ) => ({
      ...state,
      successApproveChange: true,
      detail: action.payload,
    }),
    [actions.failedApproveChangeReservation]: (
      state: State,
      action: ACTION_TYPE,
    ) => ({
      ...state,
      failedApproveChange: action.payload,
    }),
  },
  initialState,
)

/**
 ************************************************************
 * API連携 非同期アクション
 ************************************************************
 **/

const messagesParams = {
  sort: 'created',
  order: 'desc',
  per_page: MESSAGES_PER_PAGE,
}

function* fetchReservationDetail({
  payload,
}: {
  payload: {
    userId: string,
    reservationId: string,
  },
}) {
  yield put({
    type: actionTypes.SET_RESERVATION_DETAIL,
    payload: { detail: {}, messages: [] },
  })
  yield put({
    type: SET_UI_STATE,
    payload: {
      isLoading: true,
    },
  })
  try {
    const detail = yield call(
      userReservationResource.fetch,
      payload.userId,
      payload.reservationId,
    )
    const messages = yield call(
      userReservationResource.messages,
      payload.reservationId,
      messagesParams,
    )
    yield put({
      type: actionTypes.SET_RESERVATION_DETAIL,
      payload: {
        detail,
        messages: (messages || []).reverse(),
      },
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_RESERVATION_DETAIL,
      payload: e.message,
    })
  } finally {
    yield put({
      type: SET_UI_STATE,
      payload: {
        isLoading: false,
      },
    })
  }
}

function* fetchMessages({
  payload,
}: {
  payload: {
    reservationId: string,
    nextPage: number,
  },
}) {
  yield put({
    type: SET_UI_STATE,
    payload: {
      messagesFetching: true,
    },
  })

  try {
    const messages = yield call(
      userReservationResource.messages,
      payload.reservationId,
      Object.assign(messagesParams, {
        page: payload.nextPage,
      }),
    )
    yield put({
      type: actionTypes.SUCCESS_FETCH_MESSAGES,
      payload: (messages || []).reverse(),
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_FETCH_MESSAGES,
      payload: e.message,
    })
  } finally {
    yield put({
      type: SET_UI_STATE,
      payload: {
        messagesFetching: false,
      },
    })
  }
}

function* sendMessage({
  payload,
}: {
  payload: {
    userId: string,
    reservationId: string,
    text: ?string,
    fileUri: ?string,
    isAuto: ?boolean,
  },
}) {
  yield put({
    type: SET_UI_STATE,
    payload: {
      messageSending: true,
    },
  })

  try {
    yield call(
      userReservationResource.createMessage,
      payload.userId,
      payload.reservationId,
      payload.text || '',
      payload.fileUri,
      payload.isAuto,
    )
    yield put({
      type: SET_UI_STATE,
      payload: {
        message: null,
        trySendFile: null,
        sendFileUri: null,
      },
    })
    yield put({
      type: actionTypes.SUCCESS_SEND_MESSAGE,
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_SEND_MESSAGE,
      payload: e.message,
    })
  } finally {
    yield put({
      type: SET_UI_STATE,
      payload: {
        messageSending: false,
      },
    })
  }
}

function* cancelReservation({
  payload,
}: {
  payload: {
    userId: string,
    reservationId: string,
    cancelReason: number,
    description?: string,
  },
}) {
  const { userId, reservationId, cancelReason, description } = payload

  yield put({
    type: SET_UI_STATE,
    payload: {
      isLoading: true,
    },
  })

  try {
    const canceledReservation = yield call(
      userReservationResource.cancel,
      userId,
      reservationId,
      {
        lost_reason_type: cancelReason,
        lost_reason_description: description,
      },
    )
    yield put({
      type: actionTypes.SUCCESS_CANCEL_RESERVATION,
      payload: canceledReservation,
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_CANCEL_RESERVATION,
      payload: e.message,
    })
  } finally {
    yield put({
      type: SET_UI_STATE,
      payload: {
        isLoading: false,
      },
    })
  }
}

function* approveChangeReservation({
  payload,
}: {
  payload: {
    userId: string,
    reservationId: string,
    paidyPaymentId?: ?string,
    paidyChargeAmount?: ?number,
  },
}) {
  const { userId, reservationId, paidyPaymentId, paidyChargeAmount } = payload

  yield put({
    type: SET_UI_STATE,
    payload: {
      isLoading: true,
    },
  })

  let paidyParams = {}
  if (paidyPaymentId && paidyChargeAmount) {
    paidyParams = {
      paidy_payment_id: paidyPaymentId,
      paidy_charge_amount: paidyChargeAmount,
    }
  }

  try {
    const reservation = yield call(
      userReservationResource.approveChange,
      userId,
      reservationId,
      paidyParams,
    )
    yield put({
      type: actionTypes.SUCCESS_APPROVE_CHANGE_RESERVATION,
      payload: reservation,
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_APPROVE_CHANGE_RESERVATION,
      payload: e.message,
    })
  } finally {
    yield put({
      type: SET_UI_STATE,
      payload: {
        isLoading: false,
      },
    })
  }
}

function* rejectChangeReservation({
  payload,
}: {
  payload: {
    userId: string,
    reservationId: string,
  },
}) {
  const { userId, reservationId } = payload

  yield put({
    type: SET_UI_STATE,
    payload: {
      isLoading: true,
    },
  })

  try {
    const reservation = yield call(
      userReservationResource.rejectChange,
      userId,
      reservationId,
    )
    yield put({
      type: actionTypes.SUCCESS_APPROVE_CHANGE_RESERVATION,
      payload: reservation,
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_APPROVE_CHANGE_RESERVATION,
      payload: e.message,
    })
  } finally {
    yield put({
      type: SET_UI_STATE,
      payload: {
        isLoading: false,
      },
    })
  }
}

/**
 ************************************************************
 * saga
 ************************************************************
 **/

export const reservationDetailSagas = [
  takeEvery(actionTypes.FETCH_RESERVATION_DETAIL, fetchReservationDetail),
  takeEvery(actionTypes.FETCH_MESSAGES, fetchMessages),
  takeEvery(actionTypes.SEND_MESSAGE, sendMessage),
  takeEvery(actionTypes.APPROVE_CHANGE_RESERVATION, approveChangeReservation),
  takeEvery(actionTypes.REJECT_CHANGE_RESERVATION, rejectChangeReservation),
  takeEvery(actionTypes.CANCEL_RESERVATION, cancelReservation),
]



// WEBPACK FOOTER //
// ./src/redux/modules/reservationDetail.js